import { Button, Container, Stack } from 'component-library';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';

import { OrganizationUIStyle } from '@/api/rest/resources/types/organization';
import { PlotGroupGroupByEnum } from '@/api/rest/resources/types/plotGroup';
import { WidgetType } from '@/api/rest/resources/types/widget';
import { usePlotGroup } from '@/components/PlotGroups/PlotGroupsContextProvider';
import { Icon } from '@/components/widgets/a/Icon/Icon';
import { useGetActivePlotGroup } from '@/components/widgets/config';
import { getOrganizationSummary } from '@/components/widgets/config/organizationSummary';
import { LAYER_OPTIONS } from '@/components/widgets/o/Map/constants';
import { useMapLayers } from '@/components/widgets/o/Map/hooks/useMapLayers';
import { WidgetSlotRenderer } from '@/components/widgets/WidgetSlotRenderer';
import { FEATURE_FLAGS } from '@/config/constants';
import { useHasFeatureFlag } from '@/hooks/useHasFeatureFlag';
import { useUIStyle } from '@/hooks/useUIStyle';
import { useActiveWidgetSlotConfiguration } from '@/hooks/useWidgetConfigurationByOrganizationId';
import { SignedInPageLayout } from '@/layout/signed-in-page';
import { AddPlotsModal } from '@/pages/shared/staff-portal/components/AddPlotsModal';
import { isNullish } from '@/utils/booleans';

import { CreatePlotGroup } from '../components/plot-group/CreatePlotGroup';
import { useIsLimitedAccessUser } from '../hooks/useIsLimitedAccessUser';
import { useIsOrganizationMember } from '../hooks/useIsOrganizationMember';
import { useMenuItems } from '../hooks/useMenuItems';
import { useOrganizationId } from '../hooks/useOrganizationId';
import { ProjectsMap } from '../projects/components/projectsMap/ProjectsMap';
import { useCommitTaskPoller } from '../staff-portal/pages/upload-plots/hooks/useCommitTaskPoller';
import { PlotList } from './components/PlotList';

export const PlotsPage = () => {
  const { t } = useTranslation();
  const menuItems = useMenuItems();
  const [selectedPlotIds, setSelectedPlotIds] = useState<string[]>([]);
  const [addPlotsOpen, setAddPlotsOpen] = useState(false);

  const organizationId = useOrganizationId();
  const showGroupByOptions = !useUIStyle(OrganizationUIStyle.CONSERVATION);

  const isLimitedAccessUser = useIsLimitedAccessUser();
  const isOrganizationMember = useIsOrganizationMember();
  const canUploadPlots = useHasFeatureFlag(FEATURE_FLAGS.PLOTS_ONBOARDING_CLIENT_DRAFT);

  const [searchParams] = useSearchParams();
  const { state: locationState } = useLocation();

  useCommitTaskPoller({ taskId: locationState?.commitTaskId ?? null, count: locationState?.commitCount ?? 0 });
  const plotGroupId = searchParams.get('plotGroupId');

  const activePlotGroup = usePlotGroup();
  const activeFilters = useGetActivePlotGroup();
  const isFiltersSelected = activeFilters?.length > 0;
  const hasGroupByFilters = activeFilters?.some((filter) => Object.keys(PlotGroupGroupByEnum).includes(filter.key));

  // Clear stale selections when all filters are removed. When switching between
  // filters (both truthy), PlotList's defaultRowSelected effect handles re-selection.
  useEffect(() => {
    if (!isFiltersSelected) {
      setSelectedPlotIds([]);
    }
  }, [isFiltersSelected]);

  const mapLayers = useMapLayers(LAYER_OPTIONS);

  const defaultWidgetSlotsConfiguration = useDefaultWidgetConfiguration();
  const customWidgetSlotsConfiguration = useActiveWidgetSlotConfiguration(
    'default_template_portfolio',
    WidgetType.t_page,
  );

  const widgetSlotsConfiguration = customWidgetSlotsConfiguration ?? defaultWidgetSlotsConfiguration;

  return (
    <>
      <SignedInPageLayout
        items={menuItems}
        withCustomContentContainer
        navBarProps={{
          title: t('navbar.portfolio'),
          showFilters: true,
          children:
            !isLimitedAccessUser && isOrganizationMember && isNullish(plotGroupId) ? (
              <Stack direction='row' className='justify-end gap-4'>
                {/* Plot upload enabled for staff users and orgs with the onboarding flag */}
                {canUploadPlots && (
                  <Button
                    variant='outline'
                    leftAdornment={<Icon icon='ri-upload-cloud-2-line' />}
                    onClick={() => setAddPlotsOpen(true)}
                  >
                    {t('shared.organizations.actions.createPlots')}
                  </Button>
                )}
                {showGroupByOptions && (
                  <CreatePlotGroup
                    key='filtered-group'
                    organizationId={organizationId}
                    plotGroup={activePlotGroup}
                    plotIds={selectedPlotIds}
                    disabled={!isFiltersSelected || false}
                  />
                )}
              </Stack>
            ) : null,
        }}
      >
        <Container gutterWidth={{ sm: 4 }} className='gap-10' data-testid='plot-list-page'>
          <WidgetSlotRenderer nestedWidget={widgetSlotsConfiguration} />
          <div data-testid='project-map-wrapper' className='h-[60vh] animate-in fade-in'>
            <ProjectsMap setSelectedPlotIds={setSelectedPlotIds} selectedPlotIds={selectedPlotIds} layers={mapLayers} />
          </div>
          {hasGroupByFilters ? (
            <PlotList showDraftPlots={canUploadPlots} />
          ) : (
            <PlotList
              enableRowSelection={true}
              defaultRowSelected={isFiltersSelected}
              setSelectedPlotIds={setSelectedPlotIds}
              selectedPlotIds={selectedPlotIds}
              showDraftPlots={canUploadPlots}
            />
          )}
        </Container>
      </SignedInPageLayout>
      {!isNullish(organizationId) && (
        <AddPlotsModal open={addPlotsOpen} onOpenChange={setAddPlotsOpen} organizationId={organizationId} />
      )}
    </>
  );
};

const useDefaultWidgetConfiguration = () => {
  const hasConservationUi = useUIStyle(OrganizationUIStyle.CONSERVATION);

  return {
    name: 'default_template_portfolio',
    type: WidgetType.t_page,
    widgets: hasConservationUi ? [] : [getOrganizationSummary()],
  };
};
